import { ApiDonationResponse } from '@fmnts/api/next';
import * as O from 'effect/Option';
import * as P from 'effect/Predicate';
import * as Str from 'effect/String';

const optionFromNonEmptyString = O.liftPredicate(
  P.compose(Str.isString, Str.isNonEmpty),
);

/**
 * The data to be displayed in the QR Modal
 */
export interface ModalData {
  /**
   * Title of the modal
   */
  readonly title: string;
  /**
   * The link will be transformed to a QRCode to be used for
   * further checkout via a different page
   */
  readonly link: O.Option<string>;
  /**
   * If email was used, the modal shows the recipient of the link
   */
  readonly email: O.Option<string>;
  /**
   * If phone number was used, the modal shows the recipient of the link
   */
  readonly phone: O.Option<string>;
}

/**
 * Checks the response for the server for the use payment method,
 * and returns the according modal data. Currently supports:
 * - Self-checkout
 * - PayKU
 *
 * @param deps Translation implementation
 * @returns `Option` of modalData if the payment method requires it
 */
export const makeModalData = (deps: { translate: (tid: string) => string }) => {
  const fromSelfCheckoutDonation = makeFromSelfCheckoutDonation(deps);

  return (data: ApiDonationResponse): O.Option<ModalData> => {
    if (!P.isObject(data)) {
      return O.none();
    }

    if (data.payment_registration_url) {
      // Set for donations with PayKU
      return O.some(fromExternalPaymentDonation(data));
    } else if (data.self_checkout_url) {
      return O.some(fromSelfCheckoutDonation(data));
    }
    return O.none();
  };
};

const makeFromSelfCheckoutDonation =
  ({ translate }: { translate: (tid: string) => string }) =>
  (data: ApiDonationResponse): ModalData => {
    const {
      self_checkout_show_qr_code,
      self_checkout_contact_email: email,
      self_checkout_contact_phone: phone,
      self_checkout_url: link,
    } = data;
    return {
      title: translate('verify_modal_title'),
      link: self_checkout_show_qr_code
        ? optionFromNonEmptyString(link)
        : O.none(),
      email: optionFromNonEmptyString(email),
      phone: optionFromNonEmptyString(phone),
    };
  };

const fromExternalPaymentDonation = (data: ApiDonationResponse): ModalData => {
  const { payment_registration_url: link, mobile_number: phone } = data;
  return {
    title: 'PayKU',
    link: optionFromNonEmptyString(link),
    phone: optionFromNonEmptyString(phone),
    email: O.none(),
  };
};
